import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, Home as HomeIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const appointmentId = params.get("appointment_id");

  const [status, setStatus] = useState("checking"); // checking | paid | timeout | failed
  const [appt, setAppt] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    document.title = "Booking Confirmed — Daniel's Dirty Deeds";
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        setPaymentInfo(data);
        if (data.payment_status === "paid") {
          if (data.appointment_id) {
            try {
              const a = await axios.get(`${API}/appointments/${data.appointment_id}`);
              setAppt(a.data);
            } catch (err) {
              // Non-blocking — payment succeeded, appointment will load via Account page
              if (process.env.NODE_ENV !== "production") console.error(err);
            }
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error(e);
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };

    poll(0);

    return () => {
      cancelled = true;
    };
  }, [sessionId, appointmentId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid place-items-center px-4 py-10">
      <div className="comic-card-static p-8 max-w-xl w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#1E88E5]" />
            <h1 className="mt-4 font-['Bebas_Neue'] text-4xl">Confirming your payment…</h1>
            <p className="mt-2 text-[#0B132B]/80">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex w-14 h-14 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] items-center justify-center mb-4 shadow-[4px_4px_0_#0B132B]">
              <CheckCircle2 className="w-7 h-7 text-[#0B132B]" />
            </div>
            <h1 className="font-['Bebas_Neue'] text-4xl">Payment Received!</h1>
            <p className="mt-2 text-[#0B132B]/80">
              Your booking is confirmed. We'll see you soon.
            </p>
            {appt && (
              <div className="mt-5 inline-block text-left bg-[#FFC107]/40 border-[3px] border-[#0B132B] rounded-xl px-5 py-4 shadow-[4px_4px_0_#0B132B]">
                <div className="text-sm">
                  <span className="font-bold">When:</span>{" "}
                  {new Date(appt.start_at).toLocaleString(undefined, {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
                <div className="text-sm">
                  <span className="font-bold">Bins:</span> {appt.num_bins}
                </div>
                <div className="text-sm">
                  <span className="font-bold">Total paid:</span> $
                  {Number(appt.total_amount).toFixed(2)}
                </div>
                <div className="text-sm">
                  <span className="font-bold">Confirmation #:</span>{" "}
                  {String(appt.id).slice(0, 8).toUpperCase()}
                </div>
              </div>
            )}
            {!appt && paymentInfo && (
              <div className="mt-4 text-sm text-[#0B132B]/70">
                Total charged: ${(paymentInfo.amount_total / 100).toFixed(2)}{" "}
                {paymentInfo.currency?.toUpperCase()}
              </div>
            )}
            <Link to="/" className="mt-6 inline-flex comic-btn comic-btn-primary">
              <HomeIcon className="w-5 h-5" /> Back to home
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-[#FF3D00]" />
            <h1 className="mt-4 font-['Bebas_Neue'] text-4xl">Still processing…</h1>
            <p className="mt-2 text-[#0B132B]/80">
              Payment confirmation is taking longer than expected. Check your email
              shortly, or call us to confirm.
            </p>
            <Link to="/" className="mt-6 inline-flex comic-btn comic-btn-outline">
              <HomeIcon className="w-5 h-5" /> Back to home
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-[#FF3D00]" />
            <h1 className="mt-4 font-['Bebas_Neue'] text-4xl">Couldn't confirm payment</h1>
            <p className="mt-2 text-[#0B132B]/80">
              Something went wrong with payment. Please try booking again or call Dan
              at 860-250-5755.
            </p>
            <Link to="/" className="mt-6 inline-flex comic-btn comic-btn-action">
              <HomeIcon className="w-5 h-5" /> Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
